import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    ArrowLeft, Bookmark, BookmarkCheck, ExternalLink,
    Users, Zap, Lock, Sparkles, ChevronRight, Share2,
    MapPin, Briefcase, DollarSign, Heart, Shield,
    RefreshCw, TrendingUp, Check, AlertTriangle, X,
    ThumbsUp, ThumbsDown, Info,
} from 'lucide-react';
import { createPortal } from 'react-dom';

import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import RecruiterModal from '../../../components/JobsPage/JobContent/RecruiterModal';
import OutreachStrategyModal from '../../../components/Outreach/OutreachStrategyModal';
import ContactCard, { avatarBg, referralProb } from '../../../components/Referral/ContactCard';
import ContactsModal from '../../../components/Referral/ContactsModal';
import { fetchRecruiterForJob, peekRecruiterForJob, fetchJobById } from '../../../redux/JobSlice/jobSlice';
import { saveRecruiterContacts } from '../../../redux/ContactSlice/contactSlice';
import { saveJob, fetchSavedJobs, deleteSavedJob } from '../../../redux/SavedJobSlice/savedJobSlice';
import { activateSprint } from '../../../redux/ActiveSprintSlice/activeSprintSlice';

// ─── helpers ────────────────────────────────────────────────────────────────

// Scraped JDs often arrive HTML-escaped (&lt;h2&gt;…), sometimes doubly so.
// Decode until stable so the markup can actually render.
const decodeHtmlEntities = (input) => {
    let out = String(input || '');
    if (!/&(lt|gt|amp|quot|#\d+|nbsp);/i.test(out)) return out;
    const el = document.createElement('textarea');
    let prev;
    let guard = 0;
    do {
        prev = out;
        el.innerHTML = out;
        out = el.value;
        guard += 1;
    } while (out !== prev && guard < 3 && /&(lt|gt|amp);/i.test(out));
    return out;
};

// Decoded scraped HTML goes into dangerouslySetInnerHTML — strip active content.
const sanitizeJobHtml = (html) => String(html || '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, '')
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, '')
    .replace(/\son\w+\s*=\s*'[^']*'/gi, '')
    .replace(/javascript:/gi, '');

// Scraped ATS descriptions often arrive as ONE flat line — bullets flattened into
// " - Item" runs and section headers ("You will:", "Compensation and Benefits:")
// glued into the prose. Parse that back into headings / bullet lists / paragraphs.
const parseJobDescBlocks = (text) => {
    let t = String(text || '').replace(/\r\n/g, '\n').replace(/[ \t]+/g, ' ').trim();
    if (!t) return [];

    // Scraped descriptions are often cut off mid-sentence at the source. If the text
    // doesn't end in terminal punctuation (or a closer), flag it so the last block
    // renders with a trailing ellipsis.
    const truncated = !/[.!?:)\]"'’”]$/.test(t);

    // Break out short "Header:" phrases stuck mid-line (letters/spaces only, ≤ ~40
    // chars, ends with ':') so they start their own line. Matched via exec so a
    // candidate inside an already-matched phrase ("Benefits:" inside "Compensation
    // and Benefits:") can't split the phrase a second time.
    const headingRe = /[A-Z][A-Za-z''&/ -]{2,38}:(?=\s|$)/g;
    let rebuilt = '';
    let cursor = 0;
    let m;
    while ((m = headingRe.exec(t)) !== null) {
        if (m.index > 0 && /\s/.test(t[m.index - 1])) {
            rebuilt += t.slice(cursor, m.index).replace(/\s+$/, '\n');
            cursor = m.index;
        }
        headingRe.lastIndex = m.index + m[0].length;
    }
    t = rebuilt + t.slice(cursor);
    // Flattened bullets: " - Item" where the item starts with a capital, digit or $.
    t = t.replace(/\s-\s+(?=[A-Z0-9$])/g, '\n- ');

    const lines = t.split('\n').map((s) => s.trim()).filter(Boolean);
    const blocks = [];
    const isHeadingText = (s) => s.length <= 42 && /:$/.test(s);
    for (const line of lines) {
        const isBullet = /^[-•*]\s+/.test(line);
        if (isBullet) {
            const item = line.replace(/^[-•*]\s+/, '');
            // A bullet that is nothing but "Something:" is a sub-section intro
            // ("Benefits offered include:") — promote it to a heading.
            if (isHeadingText(item)) {
                blocks.push({ type: 'heading', text: item });
                continue;
            }
            const last = blocks[blocks.length - 1];
            if (last && last.type === 'list') last.items.push(item);
            else blocks.push({ type: 'list', items: [item] });
        } else if (isHeadingText(line)) {
            blocks.push({ type: 'heading', text: line });
        } else {
            // Long unbroken prose: split at sentence boundaries into ~digestible chunks.
            const sentences = line.replace(/([.!?])\s+(?=[A-Z1])/g, '$1').split('');
            let buf = '';
            for (const s of sentences) {
                buf = buf ? `${buf} ${s}` : s;
                if (buf.length >= 320) {
                    blocks.push({ type: 'para', text: buf });
                    buf = '';
                }
            }
            if (buf) blocks.push({ type: 'para', text: buf });
        }
    }
    if (truncated && blocks.length > 0) {
        const last = blocks[blocks.length - 1];
        if (last.type === 'list') last.items[last.items.length - 1] += '…';
        else last.text += '…';
    }
    return blocks;
};

const PlainJobDesc = ({ text }) => {
    const blocks = parseJobDescBlocks(text);
    return (
        <div className="text-[13.5px] text-[#334155] leading-[1.75] text-left">
            {blocks.map((b, i) => {
                if (b.type === 'heading') {
                    return <p key={i} className="font-bold text-[#0f172a] mt-4 first:mt-0 mb-1.5">{b.text}</p>;
                }
                if (b.type === 'list') {
                    return (
                        <ul key={i} className="list-disc pl-5 mb-3 space-y-1.5">
                            {b.items.map((item, j) => <li key={j}>{item}</li>)}
                        </ul>
                    );
                }
                return <p key={i} className="mb-3">{b.text}</p>;
            })}
        </div>
    );
};

const scoreColor = (s) => {
    if (s == null) return '#94A3B8';
    if (s >= 70) return '#10B981';
    if (s >= 55) return '#3F57E0';
    if (s >= 35) return '#F59E0B';
    return '#EF4444';
};

const categoryLabel = (s) => {
    if (s == null) return null;
    if (s >= 75) return { text: 'Excellent',    color: '#10B981', bg: '#f0fdf4', border: '#86efac' };
    if (s >= 60) return { text: 'Very Good',    color: '#0D9488', bg: '#f0fdfa', border: '#99f6e4' };
    if (s >= 45) return { text: 'Good',         color: '#3F57E0', bg: '#eff6ff', border: '#bfdbfe' };
    if (s >= 30) return { text: 'Average',      color: '#D97706', bg: '#fffbeb', border: '#fde68a' };
    return               { text: 'Below avg',   color: '#EF4444', bg: '#fef2f2', border: '#fecaca' };
};

const fitConfig = (label) => {
    if (!label) return null;
    const l = label.toLowerCase();
    if (l.includes('strong')) return { text: 'Strong fit',   dot: '#10B981', color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' };
    if (l.includes('good'))   return { text: 'Good fit',     dot: '#3F57E0', color: '#1e40af', bg: '#eff6ff', border: '#bfdbfe' };
    if (l.includes('weak') || l.includes('low')) return { text: 'Weak fit', dot: '#94A3B8', color: '#475569', bg: '#f8fafc', border: '#e2e8f0' };
    return                           { text: 'Possible fit', dot: '#F59E0B', color: '#92400e', bg: '#fffbeb', border: '#fde68a' };
};

// ── Similar roles: title-token overlap ──────────────────────────────────────
// Words that carry no role meaning (seniority, levels, articles, work modes).
const TITLE_FILLER = new Set([
    'the', 'a', 'an', 'of', 'and', 'or', 'for', 'to', 'in', 'at', 'with',
    'remote', 'hybrid', 'onsite', 'on-site', 'contract', 'fulltime', 'parttime',
    'jr', 'sr', 'i', 'ii', 'iii', 'iv', 'v', 'level', 'entry', 'junior', 'mid',
    'senior', 'staff', 'principal', 'associate', 'lead', 'intern', 'trainee', 'new'
]);
// Fold related tokens onto one stem so developer ≈ development ≈ engineer overlap.
const TITLE_SYNONYM = {
    developer: 'engineer', development: 'engineer', dev: 'engineer',
    engineering: 'engineer', programmer: 'engineer', swe: 'engineer', sde: 'engineer',
    mgr: 'manager', management: 'manager',
    analytics: 'analyst', analysis: 'analyst',
    designer: 'design',
    architecture: 'architect'
};
const titleTokens = (title) => {
    const tokens = new Set();
    String(title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .forEach((w) => {
            if (w.length < 2 || /^\d+$/.test(w) || TITLE_FILLER.has(w)) return;
            tokens.add(TITLE_SYNONYM[w] || w);
        });
    return tokens;
};

const timeAgo = (d) => {
    if (!d) return null;
    const date = d?.$date?.$numberLong ? new Date(parseInt(d.$date.$numberLong,10)) : new Date(d);
    if (isNaN(date.getTime())) return null;
    const diff = Math.floor((Date.now()-date.getTime())/86400000);
    const rel = diff===0 ? 'Today' : diff===1 ? 'Yesterday'
        : diff<7 ? `${diff}d ago` : diff<30 ? `${Math.floor(diff/7)}w ago`
        : `${Math.floor(diff/30)}mo ago`;
    const abs = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return { rel, abs };
};

// ─── Score ring SVG ──────────────────────────────────────────────────────────

const ScoreRing = ({ value, color, size = 80 }) => {
    const pct = Math.min(100, Math.max(0, value ?? 0));
    const r = (size - 12) / 2;
    const circ = 2 * Math.PI * r;
    return (
        <svg width={size} height={size}>
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#E6E8EE" strokeWidth={8} />
            <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
                strokeDasharray={`${(pct/100)*circ} ${circ}`} strokeLinecap="round"
                transform={`rotate(-90 ${size/2} ${size/2})`} />
            <text x="50%" y="50%" dominantBaseline="middle" textAnchor="middle"
                fontSize={size<60?11:17} fontWeight="800" fill={color}>{value??'–'}</text>
        </svg>
    );
};

// ─── Compact contact row ─────────────────────────────────────────────────────

// ─── Category card (right rail) ──────────────────────────────────────────────

const CategoryCard = ({ icon: Icon, iconColor, title, score, bullets }) => {
    const lbl = categoryLabel(score);
    const hasBullets = Array.isArray(bullets) && bullets.length > 0;
    if (!lbl && !hasBullets) return null;
    return (
        <div className="bg-white rounded-2xl border border-[#E6E8EE] p-3 flex-1 min-w-0">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${iconColor}15` }}>
                        <Icon className="w-4 h-4" style={{ color: iconColor }} />
                    </div>
                    <span className="text-[13.5px] font-bold text-[#0f172a]">{title}</span>
                </div>
                {lbl && (
                    <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full border"
                          style={{ color: lbl.color, backgroundColor: lbl.bg, borderColor: lbl.border }}>
                        {lbl.text}
                    </span>
                )}
            </div>
            {hasBullets ? (
                <ul className="flex flex-col gap-1.5 text-left">
                    {bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-2 text-[12px] text-[#334155] text-left">
                            <span className="w-1 h-1 rounded-full bg-gray-300 shrink-0 mt-1.5" />
                            {b}
                        </li>
                    ))}
                </ul>
            ) : (
                /*
                    No bullets means the posting carried nothing about this
                    category. Saying so is the honest answer — this used to fall
                    back to invented lines like "Above national median for this
                    role", which no source supported and which the reader had no
                    way to tell apart from a finding.
                */
                <p className="text-[11.5px] text-[#94A3B8] text-left italic">
                    Not enough information in this posting to assess.
                </p>
            )}
        </div>
    );
};

// ─── Main component ──────────────────────────────────────────────────────────

// `embedded` renders just the detail content (no navbar/sidebar/top bar, no own
// scroll container) so the page can live inline inside an expanded job card.
// `jobIdProp` supplies the job id when there's no /jobs/:jobId route param.
const JobDetailPage = ({ jobIdProp = null, embedded = false }) => {
    const { jobId: routeJobId } = useParams();
    const jobId = jobIdProp || routeJobId;
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const jobFromState    = location.state?.job;
    const fromSavedJobs   = location.state?.fromSavedJobs;
    const enrichedJobsArray = useSelector(s => s.jobs.enrichedJobsArray);
    const savedJobs = useSelector(s => s.savedJobs.savedJobs);
    const companies = useSelector(s => s.companies?.companies || []);
    const user = useSelector(s => s.auth.user);
    const [fetchedJob, setFetchedJob] = useState(null);
    const [jobLoadFailed, setJobLoadFailed] = useState(false);
    // Base job from the list/nav state renders instantly (title, scores, company), but it
    // does NOT include heavy detail fields like jobDesc (trimmed from the list payload).
    // We fetch the full job by id and MERGE it on top so the description loads in.
    const baseJob = enrichedJobsArray.find(j => j._id === jobId) || jobFromState || null;
    const job = (baseJob && fetchedJob) ? { ...baseJob, ...fetchedJob } : (baseJob || fetchedJob);

    const [recruiterState, setRecruiterState] = useState('idle');
    const [contactCount, setContactCount] = useState(10); // contacts to fetch (1–50); 1 credit per contact returned
    const [contactsPeeking, setContactsPeeking] = useState(true); // mount-time cache peek in flight
    const [recruiterData, setRecruiterData] = useState(null);
    const [selectedContact, setSelectedContact] = useState(null);
    const [showStrategyModal, setShowStrategyModal] = useState(false);
    const [analyzedData, setAnalyzedData] = useState(null);
    const [isSaved, setIsSaved] = useState(false);
    const [toast, setToast] = useState(null);
    const [showSimilarModal, setShowSimilarModal] = useState(false);
    const [showContactsModal, setShowContactsModal] = useState(false);
    const [showOutreachModal, setShowOutreachModal] = useState(false);

    useEffect(() => {
        if (user?._id && savedJobs.length === 0) {
            dispatch(fetchSavedJobs(user._id));
        }
    }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Always fetch the full job by id on entry — even if a trimmed version exists in the
    // feed/nav state — so detail-only fields (jobDesc, descriptionSections) are loaded.
    useEffect(() => {
        if (!jobId) return;
        let cancelled = false;
        setFetchedJob(null);
        setJobLoadFailed(false);
        dispatch(fetchJobById(jobId))
            .unwrap()
            .then((j) => { if (!cancelled) setFetchedJob(j); })
            // Only surface a hard error if we have no base job to show either.
            .catch(() => { if (!cancelled && !baseJob) setJobLoadFailed(true); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, dispatch]);

    // On load/refresh, restore previously-fetched recruiter contacts for this job
    // so the referral path panel doesn't revert to the "Find contacts" CTA.
    // This is a no-charge read-only peek (server returns null if none cached).
    useEffect(() => {
        const jid = job?._id;
        if (!jid) return; // job not resolved yet — keep the loader, effect re-runs once it loads
        let cancelled = false;
        setContactsPeeking(true);
        dispatch(peekRecruiterForJob({ jobDetails: job }))
            .unwrap()
            .then((result) => {
                if (cancelled || !result) return;
                const all = [
                    ...(result?.hiring_manager?.best_guesses || []),
                    ...(result?.recruiters?.best_guesses || []),
                ];
                if (all.length > 0) {
                    setRecruiterData(result);
                    setRecruiterState('done');
                }
            })
            .catch(() => {})
            .finally(() => { if (!cancelled) setContactsPeeking(false); });
        return () => { cancelled = true; };
    }, [job?._id]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!job) return embedded ? (
        <div className="flex items-center justify-center py-10">
            {jobLoadFailed
                ? <p className="text-[13px] text-gray-400">Job not available — it may have been removed.</p>
                : <p className="text-gray-400 text-[13px]">Loading job details…</p>}
        </div>
    ) : (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <Navbaar />
            <div className="flex flex-1 items-center justify-center">
                {jobLoadFailed ? (
                    <div className="text-center">
                        <p className="text-[15px] font-semibold text-[#0F172A]">Job not available</p>
                        <p className="text-[12.5px] text-gray-400 mt-1">This job may have been removed.</p>
                        <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-[#3F57E0] text-white text-[13px] font-semibold rounded-lg hover:bg-[#3347C8] transition-colors">Go back</button>
                    </div>
                ) : (
                    <p className="text-gray-400">Loading job details…</p>
                )}
            </div>
        </div>
    );

    // ── Derived ──────────────────────────────────────────────────────────────
    const companyFromStore = companies.find(
        c => c._id === job.companyId?._id || c._id === job.companyId
    );
    const companyDescription = job.companyId?.description || companyFromStore?.description || null;

    const companyName = job.companyId?.name || job.companyName || 'Unknown Company';
    const companyLogo = job.companyId?.companyLogo || job.companyLogo;
    const jobTitle    = job.jobPosition || job.title || 'Job Position';
    const score       = typeof job.score === 'number' ? Math.round(job.score) : null;
    const fit         = fitConfig(job.matchLabel);
    const posted      = timeAgo(job.datentime || job.postedDate || job.postedAt);
    const isAlreadySaved = savedJobs?.some(s => (s.jobId?._id||s.jobId) === job._id);

    // reasons
    const rawReasons = Array.isArray(job.reasons)
        ? job.reasons
        : (typeof job.reasons === 'string' && job.reasons.trim() ? [job.reasons.trim()] : []);
    const rankedReasonCodes = Array.isArray(job.reasonCodes) ? job.reasonCodes : [];
    const reasons = rawReasons.slice(0,6).map((r,i) => {
        if (r && typeof r === 'object') return { text: String(r.text||r.reason||'').trim(), code: r.code||rankedReasonCodes[i]||'', priority: Number(r.priority)||3 };
        return { text: String(r||'').trim(), code: rankedReasonCodes[i]||'', priority: 3 };
    }).filter(r => r.text);

    // company scores
    const hasReduxScores = job.companyScores && Object.values(job.companyScores).some(v=>Number.isFinite(Number(v))&&Number(v)>0);
    const cs = hasReduxScores ? job.companyScores : (job.companyId?.scores ? {
        compensation: job.companyId.scores.compensation?.weightedScore,
        stability: job.companyId.scores.stability?.weightedScore,
        growth: job.companyId.scores.careerGrowth?.weightedScore,
        WLB: job.companyId.scores.workLifeBalance?.weightedScore,
        toxicity: job.companyId.scores.toxicity?.weightedScore,
        referral: job.companyId.scores.referral?.weightedScore,
    } : {});
    const compS = (k) => cs[k] != null ? Math.round(Number(cs[k])) : null;

    // No category came back with a usable score. The research step still returns
    // bullet points in that case, but they are all restatements of "not available",
    // so rendering five cards of them fills the rail without informing anyone.
    const noCompanyData = ['compensation', 'WLB', 'growth', 'toxicity', 'stability']
        .every((k) => compS(k) == null);

    // score breakdown
    const scoreBreakdown = job.scoreBreakdown || null;
    const scoreAccent = score == null ? '#94A3B8' : scoreColor(score);

    // company signal circles config
    const companySignals = [
        { key: 'compensation', label: 'Compensation',     icon: DollarSign,     category: null },
        { key: 'WLB',          label: 'Work Life Balance',icon: Heart,          category: null },
        { key: 'growth',       label: 'Career Growth',    icon: TrendingUp,     category: null },
        { key: 'toxicity',     label: 'Toxicity',         icon: AlertTriangle,  category: 'toxicity' },
        { key: 'stability',    label: 'Stability',        icon: Shield,         category: null },
        { key: 'referral',     label: 'Referral',         icon: Users,          category: null },
    ];

    const signalLabel = (val, category) => {
        // A company we could not research keeps its card, so the row does not
        // collapse into blank space, but says plainly that nothing was found
        // rather than showing a rating we cannot support.
        if (val == null) return { text: 'No data', color: '#94A3B8' };
        if (category === 'toxicity') {
            if (val >= 80) return { text: 'Very Low',   color: '#10B981' };
            if (val >= 60) return { text: 'Low',        color: '#10B981' };
            if (val >= 40) return { text: 'Average',    color: '#3F57E0' };
            if (val >= 25) return { text: 'Moderate',   color: '#D97706' };
            return               { text: 'High',        color: '#EF4444' };
        }
        if (val >= 85) return { text: 'Excellent', color: '#10B981' };
        if (val >= 70) return { text: 'Very Good',  color: '#0D9488' };
        if (val >= 55) return { text: 'Good',       color: '#3F57E0' };
        if (val >= 40) return { text: 'Moderate',   color: '#D97706' };
        if (val >= 25) return { text: 'Fair',       color: '#F97316' };
        return               { text: 'Limited',     color: '#EF4444' };
    };

    // Toxicity icon reflects the *level*: the score is inverted (higher score =
    // lower toxicity), so low toxicity → thumbs up, elevated → warning, very high
    // toxicity → thumbs down.
    const toxicityVisual = (val) => {
        if (val == null)  return { Icon: AlertTriangle, color: '#94A3B8', bg: '#f1f5f9' };
        if (val >= 60)    return { Icon: ThumbsUp,      color: '#10B981', bg: '#f0fdf4' }; // low toxicity
        if (val >= 25)    return { Icon: AlertTriangle, color: '#F59E0B', bg: '#fffbeb' }; // elevated toxicity
        return            { Icon: ThumbsDown,    color: '#EF4444', bg: '#fef2f2' }; // very high toxicity
    };


    // Similar roles — ranked by shared meaningful title words with THIS job (the old
    // score-proximity sort said nothing about the role: two unrelated jobs with equal
    // scores looked "similar"). Jobs sharing no title token are excluded entirely —
    // an honest empty state beats fake suggestions. Plain computation (no hook): this
    // sits below the `if (!job)` early return, where hooks aren't allowed.
    const similar = (() => {
        const baseTokens = titleTokens(job.jobPosition || job.title);
        if (baseTokens.size === 0) return [];
        return enrichedJobsArray
            .filter(j => j._id !== job._id)
            .map(j => {
                let overlap = 0;
                titleTokens(j.jobPosition || j.title).forEach(t => { if (baseTokens.has(t)) overlap += 1; });
                return { j, overlap };
            })
            .filter(x => x.overlap > 0)
            .sort((a, b) => (b.overlap - a.overlap) || ((Number(b.j.score) || 0) - (Number(a.j.score) || 0)))
            .slice(0, 3)
            .map(x => x.j);
    })();

    // ── Recruiter ────────────────────────────────────────────────────────────
    const handleFindRecruiters = async () => {
        setRecruiterState('loading');
        setAnalyzedData(null);
        try {
            const result = await dispatch(fetchRecruiterForJob({ jobDetails: job, numberOfContacts: contactCount })).unwrap();
            const all = [...(result?.hiring_manager?.best_guesses||[]), ...(result?.recruiters?.best_guesses||[])];
            if (all.length===0) { setRecruiterState('empty'); return; }
            setRecruiterData(result);
            setRecruiterState('done');
            // Save to My Network so contacts appear under this company
            const organizationId = savedJobEntry?.organizationId;
            const companyId = savedJobEntry?.companyId || job.companyId?._id || job.companyId;
            if (organizationId && companyId) {
                dispatch(saveRecruiterContacts({ jobId: job._id, companyId, organizationId, recruiterData: result }));
            }
        } catch (err) {
            // Out of credits (402) is not "no contacts" — surface it and let the
            // user retry after topping up.
            if (err?.error === 'QuotaExceeded') {
                showToast(err?.message || 'You are out of credits — buy more to fetch contacts.');
                setRecruiterState('idle');
            } else {
                setRecruiterState('empty');
            }
        }
    };

    const allContacts = (() => {
        if (!recruiterData) return [];
        const seen = new Set();
        return [
            ...(recruiterData.hiring_manager?.best_guesses||[]),
            ...(recruiterData.recruiters?.best_guesses||[]),
        ].filter(c => {
            const key = c.profile_url || c.linkedin_url || c.name;
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        }).sort((a, b) =>
            // Server-side relevance (seniority + tenure + background overlap with
            // this user) wins; the older probability score is the fallback for
            // payloads that predate it.
            ((b.relevance?.score ?? -1) - (a.relevance?.score ?? -1))
            || ((referralProb(b)?.prob ?? -1) - (referralProb(a)?.prob ?? -1))
            || ((b.confidence_score || 0) - (a.confidence_score || 0))
        );
    })();

    // ── Helpers ──────────────────────────────────────────────────────────────
    const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null), 3000); };

    const savedJobEntry = savedJobs.find(s => (s.jobId?._id || s.jobId) === job._id);

    const handleToggleSave = () => {
        if (!user?._id) return;
        if (savedJobEntry?._id) {
            dispatch(deleteSavedJob(savedJobEntry._id));
            setIsSaved(false);
            showToast('Job removed');
        } else {
            dispatch(saveJob({ userId: user._id, jobId: job._id }));
            setIsSaved(true);
            showToast('Job saved!');
        }
    };

    const handleShare = () => {
        navigator.clipboard.writeText(window.location.href);
        showToast('Link copied!');
    };

    // Activate an outreach strategy → create an independent Daily Tasks sprint.
    // Works for any job (saved or not); a role can hold several coexisting
    // sprints (referral AND direct) since each is its own ActiveSprint row.
    const PATH_TITLE = {
        referral: 'Get an Internal Referral',
        direct: 'Contact the Hiring Team Directly',
        signal: 'Warm a Hiring Signal'
    };
    const handleActivateStrategy = async (pathId, strategy, contacts = []) => {
        if (!user?._id) { showToast('Please sign in to activate a strategy'); return false; }
        if (!job?._id) { showToast('Job not found'); return false; }
        if (!PATH_TITLE[pathId]) { showToast("This strategy can't be activated here"); return false; }
        try {
            await dispatch(activateSprint({
                userId: user._id,
                sourceType: 'job',
                sourceId: job._id,
                pathId,
                subStrategyKey: strategy?.key || null,
                strategyName: strategy?.name || null,
                pathTitle: PATH_TITLE[pathId],
                // The strategy's exact day-by-day plan, rendered verbatim on the board.
                plan: Array.isArray(strategy?.days)
                    ? strategy.days.map((d, i) => ({
                        label: d.label || `Day ${i + 1}`,
                        action: d.action || '',
                        channel: d.channel || null,
                        goal: d.goal || null
                    }))
                    : [],
                // The exact "who to contact" people chosen for this path.
                contacts: (Array.isArray(contacts) ? contacts : []).slice(0, 8).map((c) => ({
                    contactid: c.contactid || c._id || c.profile_url || c.linkedin_url || c.name,
                    name: c.name || 'Unknown',
                    title: c.title || '',
                    email: c.email || null,
                    profile_url: c.profile_url || c.linkedin_url || null,
                    profile_photo: c.profile_photo || c.photo_url || null,
                    predicted_role: c.predicted_role || c._fullData?.lead_category || null,
                    industry: c.industry || null
                })),
                companyName,
                companyWebsite: job.companyId?.website || job.companyId?.domain || job.companyWebsite || null,
                companyLogo: companyLogo || null,
                organizationId: job.companyId?.organizationId || job.companyId?._id || savedJobEntry?.organizationId || (typeof job.companyId === 'string' ? job.companyId : null),
                jobTitle
            })).unwrap();
            showToast('Strategy activated — added to your Daily Tasks');
            setShowOutreachModal(false);
            navigate('/dailytasks');
            return true;
        } catch (e) {
            showToast(typeof e === 'string' ? e : 'Could not activate strategy');
            return false;
        }
    };

    return (
        <div className={embedded ? "relative" : "relative h-screen bg-[#F6F7FB] pt-16 flex flex-col"}>
            {!embedded && <Navbaar />}

            <div className={embedded ? "" : "flex flex-1 min-h-0"}>
                {/* Sidebar */}
                {!embedded && (
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>
                )}

                {/* Main */}
                <div className={embedded ? "" : "flex-1 flex flex-col min-h-0 overflow-hidden"}>

                    {/* ── Sticky top action bar (hidden inline — the card provides context) ── */}
                    {!embedded && (
                    <div className="bg-white border-b border-[#E6E8EE] shadow-sm shrink-0 px-3 py-3 flex items-center gap-2">
                        {/* Back — extreme left */}
                        <button
                            onClick={() => fromSavedJobs ? navigate('/savedjobs', { state: { activeView: 'jobs' } }) : navigate('/')}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[13px] font-medium text-gray-600 hover:bg-gray-50 hover:text-[#3F57E0] transition-colors shrink-0"
                        >
                            <ArrowLeft className="w-4 h-4" /> Back to all jobs
                        </button>

                        {/* Breadcrumb */}
                        <div className="flex items-center gap-1 text-[12.5px] text-gray-400 min-w-0 mr-auto">
                            <button onClick={()=>navigate('/')} className="hover:text-[#3F57E0] transition-colors">Jobs</button>
                            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                            <span className="truncate max-w-[120px]">{companyName}</span>
                            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                            <span className="text-[#0f172a] font-medium truncate max-w-[180px]">{jobTitle}</span>
                        </div>

                        <button onClick={handleToggleSave}
                                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg border text-[13px] font-medium transition-all shrink-0 ${
                                    isSaved||isAlreadySaved
                                        ? 'border-[#86efac] bg-[#f0fdf4] text-[#166534]'
                                        : 'border-[#E6E8EE] bg-white text-gray-700 hover:border-gray-300'
                                }`}>
                            {isSaved||isAlreadySaved ? <BookmarkCheck className="w-4 h-4"/> : <Bookmark className="w-4 h-4"/>}
                            {isSaved||isAlreadySaved ? 'Saved' : 'Save'}
                        </button>

                        {job.applyLink ? (
                            <a href={job.applyLink} target="_blank" rel="noopener noreferrer"
                               className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#059669] hover:bg-[#047857] text-white text-[13px] font-semibold transition-all shadow-sm hover:shadow-md hover:shadow-emerald-200 shrink-0">
                                Apply on company site <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                        ) : (
                            <span className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#059669] text-white text-[13px] font-semibold opacity-40 cursor-not-allowed shrink-0">
                                Apply on company site <ExternalLink className="w-3.5 h-3.5" />
                            </span>
                        )}
                    </div>
                    )}

                    {/* ── Scrollable body (in embedded mode: plain flow, page scrolls) ── */}
                    <div className={embedded ? "" : "flex-1 overflow-y-auto"}>
                        <div className="px-4 py-4 flex flex-col gap-3">

                            {/* ── Job header card ──────────────────────────── */}
                            <div className="bg-white rounded-2xl shadow-sm p-5">
                                <div className="flex items-start gap-4">
                                    {/* Left: all content */}
                                    <div className="flex-1 min-w-0">
                                        {/* Logo + title/meta */}
                                        <div className="flex items-start gap-3">
                                            <div className="w-12 h-12 rounded-xl border border-[#E6E8EE] bg-white flex items-center justify-center shrink-0 overflow-hidden">
                                                {companyLogo ? (
                                                    <img src={companyLogo} alt={companyName}
                                                         className="w-full h-full object-contain p-1"
                                                         onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}} />
                                                ) : null}
                                                <div className="w-full h-full flex items-center justify-center text-white text-lg font-extrabold rounded-xl"
                                                     style={{ background: avatarBg(companyName), display: companyLogo ? 'none' : 'flex' }}>
                                                    {companyName[0]}
                                                </div>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                {/* Title + fit pill */}
                                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                                    <h1 className="text-[18px] font-extrabold text-[#0f172a] leading-tight">{jobTitle}</h1>
                                                    {fit && (
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border shrink-0"
                                                              style={{ backgroundColor: fit.bg, color: fit.color, borderColor: fit.border }}>
                                                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: fit.dot }} />
                                                            {fit.text}
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Meta row */}
                                                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-[#64748b]">
                                                    <span className="font-semibold text-[#334155]">{companyName}</span>
                                                    {job.jobLocation && <><span className="text-gray-300">·</span><span className="flex items-center gap-1"><MapPin className="w-3 h-3 shrink-0"/>{job.jobLocation}</span></>}
                                                    {(job.jobType||job.workType) && <><span className="text-gray-300">·</span><span className="flex items-center gap-1"><Briefcase className="w-3 h-3 shrink-0"/>{job.jobType||job.workType}</span></>}
                                                    {(job.jobMinSalary||job.jobMaxSalary)
                                                        ? <><span className="text-gray-300">·</span><span className="flex items-center gap-1"><DollarSign className="w-3 h-3 shrink-0"/>${job.jobMinSalary?.toLocaleString()||'0'}–${job.jobMaxSalary?.toLocaleString()||'0'}</span></>
                                                        : <><span className="text-gray-300">·</span><span className="text-gray-400">Salary not listed</span></>
                                                    }
                                                    {posted && <><span className="text-gray-300">·</span><span className="text-gray-400">Posted {posted.rel} · {posted.abs}</span></>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Company description */}
                                        {companyDescription && (
                                            <p className="mt-3 text-[12px] text-[#94a3b8] leading-relaxed text-left">
                                                {companyDescription}
                                            </p>
                                        )}

                                        {/* Company signals — always rendered. Hiding the row when no
                                            score resolved left a gap in the layout and told the user
                                            nothing; the cards now stay and read "No data". */}
                                        {(
                                            <div className={`${companyDescription ? 'mt-4' : 'mt-5'} flex items-start justify-between gap-2`}>
                                        {[
                                            { key: 'compensation', label: 'Compensation',     icon: DollarSign,    iconColor: '#10B981', iconBg: '#f0fdf4',  bulletsKey: 'compensation',   tooltip: { title: 'Compensation', body: 'Reflects salary competitiveness, benefits quality, and pay transparency based on employee reports and market data.' } },
                                            { key: 'WLB',          label: 'Work Life Balance', icon: Heart,         iconColor: '#8B5CF6', iconBg: '#f5f3ff',  bulletsKey: 'workLifeBalance', tooltip: { title: 'Work Life Balance', body: 'Measures flexibility, average working hours, remote options, and time-off culture reported by employees.' } },
                                            { key: 'growth',       label: 'Career Growth',    icon: TrendingUp,    iconColor: '#3F57E0', iconBg: '#EEF1FE',  bulletsKey: 'careerGrowth',   tooltip: { title: 'Career Growth', body: 'Indicates opportunities for learning, promotions, mentorship, and upward mobility within the company.' } },
                                            { key: 'toxicity',     label: 'Toxicity',         icon: AlertTriangle, iconColor: '#F59E0B', iconBg: '#fffbeb',  category: 'toxicity', bulletsKey: 'toxicity', tooltip: { title: 'Toxicity', body: 'Signals workplace culture red flags such as poor management, high burnout, or negative employee sentiment. Lower is better.' } },
                                            { key: 'stability',    label: 'Stability',        icon: Shield,        iconColor: '#0D9488', iconBg: '#f0fdfa',  bulletsKey: 'stability',      tooltip: { title: 'Stability', body: 'Reflects company financial health, layoff history, funding status, and overall job security.' } },
                                            { key: 'referral',     label: 'Referral',         icon: Users,         iconColor: '#6366F1', iconBg: '#eef2ff',  tooltip: { title: 'Referral Network', body: 'How strong the referral path and employee advocacy signals look for this employer (higher = better).' } },
                                        ].map(({ key, label, icon: Icon, iconColor, iconBg, category, bulletsKey, tooltip }) => {
                                            const val = compS(key);
                                            const lbl = signalLabel(val, category);
                                            let DispIcon = Icon, dispColor = iconColor, dispBg = iconBg;
                                            if (category === 'toxicity') {
                                                const tv = toxicityVisual(val);
                                                DispIcon = tv.Icon; dispColor = tv.color; dispBg = tv.bg;
                                            }
                                            // Rollover shows this company's REAL insight bullets (same data as the
                                            // Company Insights section) when available; generic explainer otherwise.
                                            const bullets = bulletsKey
                                                ? (job.companyId?.scores?.[bulletsKey]?.bulletPoints || []).filter(Boolean).slice(0, 3)
                                                : [];
                                            return (
                                                <div key={key} className="relative group flex flex-col items-center gap-1.5 flex-1 cursor-pointer">
                                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                                                         style={{ backgroundColor: dispBg }}>
                                                        <DispIcon className="w-[18px] h-[18px]" style={{ color: dispColor }} />
                                                    </div>
                                                    <span className="text-[11px] text-gray-400 text-center leading-tight">{label}</span>
                                                    <span className="text-[12px] font-bold text-center" style={{ color: val != null ? lbl.color : '#cbd5e1' }}>
                                                        {lbl.text}
                                                    </span>
                                                    {tooltip && (
                                                        <div className={`pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-2 ${bullets.length ? 'w-72' : 'w-56'} bg-gray-900 text-white rounded-lg px-3 py-2.5 text-left opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-50 shadow-xl`}>
                                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
                                                            <p className="text-[11px] font-semibold mb-1">{tooltip.title}{val != null && bullets.length > 0 ? ` · ${lbl.text}` : ''}</p>
                                                            {val == null ? (
                                                                // Say why the card is blank. Showing the generic
                                                                // "what this measures" copy here read as though a
                                                                // rating existed and the user had missed it.
                                                                <p className="text-[11px] text-gray-300 leading-relaxed">
                                                                    We could not find enough public information about this
                                                                    company to rate {tooltip.title.toLowerCase()}.
                                                                </p>
                                                            ) : bullets.length > 0 ? (
                                                                <ul className="space-y-1">
                                                                    {bullets.map((b, i) => (
                                                                        <li key={i} className="flex gap-1.5 text-[11px] text-gray-300 leading-relaxed">
                                                                            <span className="text-gray-500 shrink-0">•</span>
                                                                            <span>{b}</span>
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            ) : (
                                                                <p className="text-[11px] text-gray-300 leading-relaxed">{tooltip.body}</p>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                        </div>
                                        )}
                                    </div>

                                    {/* Right: score ring */}
                                    {score !== null && (
                                        <div className="shrink-0 flex flex-col items-center gap-1 text-center">
                                            <ScoreRing value={score} color={scoreColor(score)} size={80} />
                                            <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: scoreColor(score) }}>Match Score</p>
                                            <p className="text-[10px] text-gray-400">Out of 100</p>
                                        </div>
                                    )}
                                </div>

                                {/* Action strip */}
                                <div className="flex items-center gap-2 mt-5 flex-wrap">
                                    <div className="relative group">
                                        <button onClick={recruiterState==='done' && allContacts.length>0 ? () => setShowOutreachModal(true) : handleFindRecruiters}
                                                disabled={recruiterState==='loading'}
                                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[13px] font-semibold transition-colors disabled:opacity-60">
                                            <Users className="w-4 h-4"/>
                                            {recruiterState==='done' && allContacts.length>0 ? 'Choose your outreach strategy' : 'Show referral path'}
                                            {recruiterState==='done' && allContacts.length>0 && (
                                                <span className="bg-white/25 text-white text-[11px] font-bold px-1.5 py-0.5 rounded-full">
                                                    {allContacts.length}
                                                </span>
                                            )}
                                        </button>
                                        <div className="absolute bottom-full left-0 mb-2 px-3 py-1.5 bg-[#0f172a] text-white text-[11.5px] rounded-lg max-w-[220px] opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                            {recruiterState==='done' && allContacts.length>0
                                                ? 'Pick an outreach strategy for your contacts.'
                                                : `Find verified contacts at ${companyName} who can refer you · up to ${contactCount} credit${contactCount!==1?'s':''}.`}
                                            <div className="absolute top-full left-4 border-4 border-transparent border-t-[#0f172a]" />
                                        </div>
                                    </div>

                                    {/* Removed from this strip:
                                        - "Tailor my resume" — only raised a "coming soon" toast.
                                        - "Company insights" — redundant. The same scores are on the
                                          signal row above (hover shows each category's bullets) and
                                          in the Company insights section in the right rail. */}

                                    <button onClick={() => showToast('Sharing — coming soon!')}
                                            className="ml-auto flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-medium text-gray-500 hover:bg-gray-50 transition-colors">
                                        <Share2 className="w-4 h-4"/> Share
                                    </button>
                                </div>
                            </div>

                            {/* ── Two-column layout ─────────────────────────── */}
                            <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-3 items-start">

                                {/* ── Left column ──────────────────────────── */}
                                <div className="flex flex-col gap-3 min-w-0">

                                    {/* AI Summary */}
                                    {reasons.length > 0 && (
                                        <div className="rounded-2xl border border-[#E6E8EE] shadow-sm overflow-hidden bg-white">
                                            {/* Header */}
                                            <div className="flex items-center gap-3 px-5 py-3.5" style={{ background: 'linear-gradient(to right, #f4f6f9, #ffffff)' }}>
                                                <div className="w-8 h-8 rounded-xl bg-[#3F57E0] flex items-center justify-center shrink-0">
                                                    <Sparkles className="w-4 h-4 text-white" />
                                                </div>
                                                <div className="text-left flex-1 min-w-0">
                                                    <p className="text-[14px] font-bold text-[#0f172a]">AI summary · why this fits</p>
                                                    <p className="text-[11.5px] text-[#6366f1]/80">Generated from your profile + this job spec</p>
                                                </div>
                                                {similar.length > 0 && (
                                                    <button onClick={() => setShowSimilarModal(true)}
                                                            className="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E6E8EE] bg-white hover:border-[#3F57E0] hover:text-[#3F57E0] text-[12px] font-medium text-gray-600 transition-colors">
                                                        <ChevronRight className="w-3.5 h-3.5" />
                                                        Similar roles
                                                    </button>
                                                )}
                                            </div>

                                            {/* Body */}
                                            <div className="px-5 py-5 border-t border-[#E6E8EE]">
                                                {/* Summary sentence */}
                                                {(() => {
                                                    const summaryText = job.jdSummary || (() => {
                                                        const title = job.jobPosition || job.title || 'This role';
                                                        const preferredDept = Array.isArray(user?.preferredDepartment) && user.preferredDepartment[0] ? user.preferredDepartment[0] : null;
                                                        const compensationText = 'your compensation preference';
                                                        return preferredDept
                                                            ? `${title} lines up with your ${preferredDept} focus and ${compensationText}, so it is worth a closer look.`
                                                            : `${title} matches your target role and ${compensationText}, with the strongest signal coming from your ranking model.`;
                                                    })();
                                                    return <p className="text-[14px] text-[#334155] italic leading-relaxed mb-4 text-left">{summaryText}</p>;
                                                })()}

                                                {/* Highlights */}
                                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3 text-left">Highlights for you</p>
                                                <ul className="flex flex-col gap-3">
                                                    {reasons.map((r, i) => {
                                                        const isPositive = !(r.code.includes('MISS') || r.code.includes('LOW'));
                                                        return (
                                                            <li key={i} className="flex items-start gap-3 text-[13.5px] text-[#334155]">
                                                                {isPositive ? (
                                                                    <svg className="w-4 h-4 shrink-0 mt-0.5 text-[#10B981]" viewBox="0 0 16 16" fill="none">
                                                                        <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    </svg>
                                                                ) : (
                                                                    <span className="w-4 h-4 shrink-0 mt-0.5 flex items-center justify-center">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
                                                                    </span>
                                                                )}
                                                                {r.text}
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    {/* Match breakdown */}
                                    {scoreBreakdown && (() => {
                                        const w = scoreBreakdown?.weights || {};
                                        const rows = [
                                            { label: 'Title match',    max: Number(w.titleMatch)||25,      val: Math.round(scoreBreakdown.titleMatch||scoreBreakdown.title||0) },
                                            { label: 'Compensation',   max: Number(w.compensation)||20,    val: Math.round(scoreBreakdown.compensation||0) },
                                            { label: 'Competition',    max: Number(w.competition)||25,     val: Math.round(scoreBreakdown.competition||0) },
                                            { label: 'Recency',        max: Number(w.recency)||15,         val: Math.round(scoreBreakdown.recency||0) },
                                            { label: 'Industry fit',   max: Number(w.industryFit)||15,     val: Math.round(scoreBreakdown.industryFit||scoreBreakdown.industry||0) },
                                            { label: 'Education fit',  max: Number(w.educationMatch)||10,  val: Math.round(scoreBreakdown.educationMatch||0) },
                                            { label: 'Experience fit', max: Number(w.experienceMatch)||10, val: Math.round(scoreBreakdown.experienceMatch||0) },
                                        ];
                                        return (
                                            <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm p-4">
                                                <h2 className="text-[13px] font-bold text-[#94a3b8] uppercase tracking-wider mb-3">Match Breakdown</h2>
                                                {rows.map((row) => (
                                                    <div key={row.label} className="flex items-center gap-3 mb-2">
                                                        <span className="w-[110px] shrink-0 text-[12.5px] text-[#64748b]">{row.label}</span>
                                                        <div className="flex-1 h-[4px] bg-[#f1f5f9] rounded-full overflow-hidden">
                                                            <div className="h-full rounded-full bg-[#3F57E0]"
                                                                 style={{ width: `${Math.max(0,Math.min(100,(row.val/row.max)*100))}%` }} />
                                                        </div>
                                                        <span className="w-6 shrink-0 text-right text-[12px] font-medium text-[#334155]">{Math.max(0,row.val)}</span>
                                                    </div>
                                                ))}
                                                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-[#f1f5f9]">
                                                    <span className="w-[110px] shrink-0 text-[12.5px] font-semibold text-[#334155]">Total</span>
                                                    <div className="flex-1 h-[4px] bg-[#f1f5f9] rounded-full overflow-hidden">
                                                        <div className="h-full rounded-full"
                                                             style={{ width:`${Math.max(0,Math.min(100,score||0))}%`, backgroundColor: scoreAccent }} />
                                                    </div>
                                                    <span className="w-[52px] shrink-0 text-right text-[12.5px] font-bold" style={{ color: scoreAccent }}>{Math.max(0,score||0)} / 100</span>
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* About this role */}
                                    {(job.jobDesc || job.description) && (() => {
                                        const raw = decodeHtmlEntities(job.jobDesc || job.description || '');
                                        const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                                        return (
                                            <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm overflow-hidden">
                                                {/* Header */}
                                                <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#E6E8EE]">
                                                    <h2 className="text-[14px] font-bold text-[#0f172a]">About this role</h2>
                                                    {job.applyLink && (
                                                        <a href={job.applyLink} target="_blank" rel="noopener noreferrer"
                                                           className="text-[12px] text-[#3F57E0] font-medium flex items-center gap-1 hover:underline shrink-0">
                                                            View full JD <ExternalLink className="w-3 h-3"/>
                                                        </a>
                                                    )}
                                                </div>
                                                {/* Content */}
                                                <div className="px-5 py-4 text-left">
                                                    {isHtml ? (
                                                        <div
                                                            className="job-desc-html text-[13.5px] text-[#334155] leading-[1.75] text-left"
                                                            dangerouslySetInnerHTML={{ __html: sanitizeJobHtml(raw) }}
                                                        />
                                                    ) : (
                                                        <PlainJobDesc text={raw} />
                                                    )}
                                                </div>
                                                <style>{`
                                                    .job-desc-html { text-align: left; }
                                                    .job-desc-html p { margin-bottom: 0.85rem; text-align: left; }
                                                    .job-desc-html ul { list-style: disc; padding-left: 1.4rem; margin-bottom: 0.85rem; }
                                                    .job-desc-html ol { list-style: decimal; padding-left: 1.4rem; margin-bottom: 0.85rem; }
                                                    .job-desc-html li { margin-bottom: 0.35rem; }
                                                    .job-desc-html h1,.job-desc-html h2,.job-desc-html h3 { font-weight: 700; color: #0f172a; margin-top: 1.25rem; margin-bottom: 0.5rem; }
                                                    .job-desc-html h1 { font-size: 1rem; }
                                                    .job-desc-html h2 { font-size: 0.9375rem; }
                                                    .job-desc-html h3 { font-size: 0.875rem; }
                                                    .job-desc-html strong, .job-desc-html b { font-weight: 600; color: #0f172a; }
                                                    .job-desc-html a { color: #3F57E0; text-decoration: underline; }
                                                    .job-desc-html br + br { display: none; }
                                                `}</style>
                                            </div>
                                        );
                                    })()}

                                    {/* Loading placeholder while the full description fetches */}
                                    {!(job.jobDesc || job.description) && !fetchedJob && !jobLoadFailed && (
                                        <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm overflow-hidden">
                                            <div className="px-5 py-3.5 border-b border-[#E6E8EE]">
                                                <h2 className="text-[14px] font-bold text-[#0f172a]">About this role</h2>
                                            </div>
                                            <div className="px-5 py-4 flex items-center gap-2 text-[13px] text-[#64748b]">
                                                <span className="inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-[#3F57E0] animate-spin" />
                                                Loading description…
                                            </div>
                                        </div>
                                    )}

                                    {/*
                                        The fetch finished and there is still no description.
                                        Previously both this case and the failed-load case fell
                                        through every branch, so the column simply ended after the
                                        match breakdown — no heading, no message, nothing to
                                        distinguish "this posting has no description" from a bug.
                                        Some ATS boards return an empty body, so this is a real
                                        state, and the apply link is the useful thing to offer.
                                    */}
                                    {!(job.jobDesc || job.description) && (fetchedJob || jobLoadFailed) && (
                                        <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm overflow-hidden">
                                            <div className="px-5 py-3.5 border-b border-[#E6E8EE]">
                                                <h2 className="text-[14px] font-bold text-[#0f172a]">About this role</h2>
                                            </div>
                                            <div className="px-5 py-4 text-left">
                                                <p className="text-[13px] text-[#64748b]">
                                                    {jobLoadFailed
                                                        ? 'We could not load the description for this role.'
                                                        : 'This posting did not include a description.'}
                                                </p>
                                                {job.applyLink && (
                                                    <a href={job.applyLink} target="_blank" rel="noopener noreferrer"
                                                       className="mt-2 inline-flex items-center gap-1 text-[12.5px] text-[#3F57E0] font-medium hover:underline">
                                                        Read it on the company site <ExternalLink className="w-3 h-3"/>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Right rail ───────────────────────────── */}
                                <div className="flex flex-col gap-3">

                                    {/* Referral path card */}
                                    <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm overflow-hidden">
                                        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[#EFF1F5]">
                                            <div className="flex items-center gap-2">
                                                <Users className="w-4 h-4 text-[#3F57E0]"/>
                                                <span className="text-[13.5px] font-bold text-[#0f172a]">Referral path</span>
                                            </div>
                                            {recruiterState==='done' && (
                                                <span className="text-[11px] font-semibold text-[#3F57E0] bg-[#EEF1FE] px-2 py-0.5 rounded-full">
                                                    {allContacts.length} contact{allContacts.length!==1?'s':''}
                                                </span>
                                            )}
                                        </div>

                                        <div className="p-3">
                                            {recruiterState==='idle' && contactsPeeking && (
                                                <div className="flex items-center justify-center gap-2 py-6 text-gray-400">
                                                    <RefreshCw className="w-4 h-4 animate-spin"/> Loading contacts…
                                                </div>
                                            )}
                                            {recruiterState==='idle' && !contactsPeeking && (
                                                <div className="flex flex-col gap-3 py-1">
                                                    <div className="flex items-start gap-2.5">
                                                        <div className="w-7 h-7 rounded-lg bg-[#EEF1FE] flex items-center justify-center shrink-0 mt-0.5">
                                                            <Users className="w-3.5 h-3.5 text-[#3F57E0]" />
                                                        </div>
                                                        <p className="text-[12.5px] text-[#475569] leading-snug">
                                                            Find verified contacts at <span className="font-semibold text-[#0f172a]">{companyName}</span> who can refer you.
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-col gap-1.5">
                                                        <span className="text-[12px] font-medium text-[#475569]">Contacts to fetch</span>
                                                        <div className="grid grid-cols-5 gap-1">
                                                            {[10, 20, 30, 40, 50].map(n => (
                                                                <button key={n} onClick={()=>setContactCount(n)}
                                                                        className={`py-1 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                                                                            contactCount===n
                                                                                ? 'bg-[#3F57E0] border-[#3F57E0] text-white'
                                                                                : 'bg-white border-[#E6E8EE] text-[#475569] hover:border-[#3F57E0] hover:text-[#3F57E0]'
                                                                        }`}>
                                                                    {n}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <button onClick={handleFindRecruiters}
                                                            className="w-full py-2 rounded-xl bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[13px] font-semibold transition-colors flex items-center justify-center gap-2">
                                                        <Users className="w-4 h-4"/> Find contacts · up to {contactCount} credit{contactCount!==1?'s':''}
                                                    </button>
                                                    <p className="text-[11px] text-gray-400 text-center -mt-1">1 credit per contact found — you only pay for what we find.</p>
                                                </div>
                                            )}
                                            {recruiterState==='loading' && (
                                                <div className="flex items-center justify-center gap-2 py-6 text-gray-400">
                                                    <RefreshCw className="w-4 h-4 animate-spin"/> Searching contacts…
                                                </div>
                                            )}
                                            {recruiterState==='empty' && (
                                                <p className="text-[12.5px] text-gray-400 text-center py-4">No verified contacts found.</p>
                                            )}
                                            {recruiterState==='done' && (
                                                <div className="flex flex-col gap-2">
                                                    <p className="text-[11px] text-gray-400 leading-snug px-1">
                                                        Ranked against your resume: how senior they are, how long they've been at {companyName}, and how much of your background they share. Recruiters and the managers this role reports into come first.
                                                    </p>
                                                    {allContacts.slice(0, 5).map((c,i) => (
                                                        <ContactCard key={i} contact={c} onSelect={contact=>{setSelectedContact(contact);setShowOutreachModal(true);}} />
                                                    ))}
                                                    {/*
                                                        Always shown, never only when the list
                                                        overflows five. The modal is the only place
                                                        carrying the ranking, the tier, the caveats
                                                        and how to approach each person — gating its
                                                        door on the count made all of that
                                                        unreachable for a short contact list.
                                                    */}
                                                    <button onClick={() => setShowContactsModal(true)}
                                                            className="w-full py-2 rounded-xl bg-[#EEF1FE] hover:bg-[#E0E6FC] text-[#3F57E0] text-[12.5px] font-semibold transition-colors flex items-center justify-center gap-1">
                                                        {allContacts.length > 5 ? `View all ${allContacts.length} contacts` : 'Contact details'}
                                                        <ChevronRight className="w-3.5 h-3.5" />
                                                    </button>
                                                    {allContacts.length > 0 && (
                                                        <button onClick={() => setShowOutreachModal(true)}
                                                                className="w-full mt-1 py-2 rounded-xl bg-[#3F57E0] hover:bg-[#3347c8] text-white text-[13px] font-semibold transition-colors">
                                                            Choose your outreach strategy
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Company insights heading */}
                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-1">Company insights</p>

                                    {/* Company score cards — real bullet points */}
                                    {noCompanyData ? (
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-center">
                                            <Info className="w-8 h-8 text-gray-300 mx-auto mb-2.5" />
                                            <p className="text-[13px] font-semibold text-gray-700">
                                                No company insights available
                                            </p>
                                            <p className="text-[11px] text-gray-500 mt-2 leading-relaxed">
                                                We could not find enough public information about{' '}
                                                {job.companyName || 'this company'} to rate compensation,
                                                work-life balance, stability, career growth or culture.
                                                This is common for smaller and privately held employers.
                                            </p>
                                            <p className="text-[11px] text-gray-500 mt-2.5 leading-relaxed">
                                                The referral contacts above are the fastest way to learn
                                                what it is actually like to work here.
                                            </p>
                                        </div>
                                    ) : (
                                    <>
                                    <CategoryCard icon={DollarSign} iconColor="#10B981" title="Compensation"
                                        score={compS('compensation')}
                                        bullets={
                                            job.companyId?.scores?.compensation?.bulletPoints || undefined
                                        }
                                    />
                                    <CategoryCard icon={Heart} iconColor="#8B5CF6" title="Work-life balance"
                                        score={compS('WLB')}
                                        bullets={
                                            job.companyId?.scores?.workLifeBalance?.bulletPoints || undefined
                                        }
                                    />
                                    <CategoryCard icon={Shield} iconColor="#0D9488" title="Stability"
                                        score={compS('stability')}
                                        bullets={
                                            job.companyId?.scores?.stability?.bulletPoints || undefined
                                        }
                                    />
                                    <CategoryCard icon={TrendingUp} iconColor="#3F57E0" title="Career growth"
                                        score={compS('growth')}
                                        bullets={
                                            job.companyId?.scores?.careerGrowth?.bulletPoints || undefined
                                        }
                                    />
                                    <CategoryCard icon={toxicityVisual(compS('toxicity')).Icon} iconColor={toxicityVisual(compS('toxicity')).color} title="Toxicity"
                                        score={compS('toxicity')}
                                        bullets={
                                            job.companyId?.scores?.toxicity?.bulletPoints || undefined
                                        }
                                    />
                                    </>
                                    )}

                                    {/* Similar roles */}
                                    {similar.length > 0 && (
                                        <div className="bg-white rounded-2xl border border-[#E6E8EE] shadow-sm p-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="text-[13.5px] font-bold text-[#0f172a]">Similar roles</h3>
                                                <span className="text-[11px] text-gray-400">{similar.length} matches</span>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                {similar.map((j, i) => {
                                                    const s = Number.isFinite(Number(j.score)) ? Math.round(j.score) : null;
                                                    const cn = j.companyId?.name || j.companyName || 'Company';
                                                    const logo = j.companyId?.companyLogo || j.companyLogo;
                                                    return (
                                                        <button key={i} onClick={()=>navigate('/jobs/'+j._id,{state:{job:j}})}
                                                                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-gray-50 transition-colors text-left w-full">
                                                            <div className="w-9 h-9 rounded-lg border border-[#E6E8EE] bg-white flex items-center justify-center shrink-0 overflow-hidden">
                                                                {logo ? (
                                                                    <img src={logo} alt={cn}
                                                                         className="w-full h-full object-contain p-0.5"
                                                                         onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }} />
                                                                ) : null}
                                                                <div className="w-full h-full flex items-center justify-center text-white text-[11px] font-bold rounded-lg"
                                                                     style={{ background: avatarBg(cn), display: logo ? 'none' : 'flex' }}>
                                                                    {cn.slice(0,2).toUpperCase()}
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-[13px] font-semibold text-[#0f172a] truncate">{j.jobPosition||j.title}</p>
                                                                <p className="text-[11.5px] text-gray-400 truncate">{cn}</p>
                                                            </div>
                                                            <span className="text-[13px] font-bold shrink-0" style={{ color: scoreColor(s) }}>{s ?? '—'}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Outreach strategy flow (new two-path design) */}
            <OutreachStrategyModal
                open={showOutreachModal}
                onClose={() => setShowOutreachModal(false)}
                context="job"
                companyName={companyName}
                jobTitle={jobTitle}
                contacts={allContacts}
                onActivate={handleActivateStrategy}
                onViewTasks={() => { setShowOutreachModal(false); navigate('/dailytasks'); }}
            />

            {/* Legacy strategy modal (deep AI analysis / email + DM tooling) */}
            {showStrategyModal && (
                <RecruiterModal
                    job={job} data={recruiterData}
                    onClose={()=>setShowStrategyModal(false)}
                    analysisAlreadyDone={!!analyzedData}
                    preAnalyzedData={analyzedData}
                    onAnalysisComplete={d=>setAnalyzedData(d)}
                />
            )}

            {/* All contacts modal */}
            <ContactsModal
                open={showContactsModal}
                onClose={() => setShowContactsModal(false)}
                contacts={allContacts}
                companyName={companyName}
                jobTitle={jobTitle}
                onChooseStrategy={() => setShowOutreachModal(true)}
            />

            {/* Similar roles modal */}
            {showSimilarModal && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowSimilarModal(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl mx-4 flex flex-col max-h-[80vh]">
                        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E6E8EE] shrink-0">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-xl bg-[#EEF1FE] flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-[#3F57E0]" />
                                </div>
                                <div>
                                    <p className="text-[14px] font-bold text-[#0f172a]">Similar roles</p>
                                    <p className="text-[11.5px] text-gray-400">{similar.length} jobs matching your profile</p>
                                </div>
                            </div>
                            <button onClick={() => setShowSimilarModal(false)}
                                    className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="overflow-y-auto flex flex-col gap-2 px-5 py-4">
                            {similar.map((j) => {
                                const s = Number.isFinite(Number(j.score)) ? Math.round(j.score) : null;
                                const cn = j.companyId?.name || j.companyName || 'Company';
                                const logo = j.companyId?.companyLogo || j.companyLogo;
                                const jTitle = j.jobPosition || j.title || 'Job';
                                const jFit = fitConfig(j.matchLabel);
                                return (
                                    <button key={j._id}
                                            onClick={() => { setShowSimilarModal(false); navigate('/jobs/'+j._id, { state: { job: j } }); }}
                                            className="flex items-center gap-3 p-3 rounded-xl border border-[#E6E8EE] hover:border-[#3F57E0] hover:bg-[#f8f9ff] transition-all text-left">
                                        <div className="w-10 h-10 rounded-xl border border-[#E6E8EE] bg-white flex items-center justify-center shrink-0 overflow-hidden">
                                            {logo
                                                ? <img src={logo} alt={cn} className="w-full h-full object-contain p-0.5" onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}} />
                                                : null}
                                            <div className="w-full h-full flex items-center justify-center text-white text-sm font-bold rounded-xl"
                                                 style={{ background: avatarBg(cn), display: logo ? 'none' : 'flex' }}>
                                                {cn[0]}
                                            </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-[13.5px] font-semibold text-[#0f172a] truncate">{jTitle}</p>
                                            <p className="text-[12px] text-gray-400 truncate">{cn}{j.jobLocation ? ` · ${j.jobLocation}` : ''}</p>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {jFit && (
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border"
                                                      style={{ color: jFit.color, backgroundColor: jFit.bg, borderColor: jFit.border }}>
                                                    {jFit.text}
                                                </span>
                                            )}
                                            <div className="flex flex-col items-center">
                                                <span className="text-[15px] font-extrabold" style={{ color: scoreColor(s) }}>{s ?? '—'}</span>
                                                <span className="text-[9px] text-gray-400 uppercase tracking-wider">score</span>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Toast */}
            {toast && createPortal(
                <div className="fixed bottom-6 right-6 z-[9999] bg-[#0f172a] text-white text-[13px] font-medium px-4 py-3 rounded-xl shadow-xl flex items-center gap-2 animate-pulse">
                    <Check className="w-4 h-4 text-[#34d399]"/> {toast}
                </div>,
                document.body
            )}
        </div>
    );
};

export default JobDetailPage;
